/**
 * Integrations API client
 *
 * Handles Asana, Google Calendar, and other integration configuration.
 */

import { fetchApi } from './client';

// Types for Asana configuration
export interface AsanaProjectMapping {
  asanaProjectGid: string;
  lifeguideProjectName: string;
  sections: Record<string, string>; // lifeguide section path -> asana section gid
}

export interface AsanaConfig {
  enabled: boolean;
  workspaceGid: string | null;
  workspaceName: string | null;
  projectMappings: Record<string, AsanaProjectMapping>;
  defaultProject: string;
  customFields: {
    priority: string | null;
    urgency: string | null;
  };
  syncSettings: {
    enableOutbound: boolean;
    enableInbound: boolean;
    conflictResolution: 'field_merge' | 'lifeguide_wins' | 'asana_wins';
    pollIntervalMinutes: number;
  };
  lastSync: string | null;
  syncStatus: 'healthy' | 'error' | 'not_configured';
  syncedTaskCount: number;
}

export interface AsanaWorkspace {
  gid: string;
  name: string;
}

export interface AsanaProject {
  gid: string;
  name: string;
  sections: Array<{ gid: string; name: string }>;
}

export interface AsanaSyncStatus {
  status: 'ok' | 'error';
  syncedTasks: number;
  syncedProjects: number;
  lastPoll: string | null;
  outboundEnabled: boolean;
  inboundEnabled: boolean;
  asanaUser: string | null;
}

// Get current Asana configuration
export async function getAsanaConfig(): Promise<AsanaConfig> {
  return fetchApi<AsanaConfig>('/integrations/asana/config');
}

// Update Asana configuration
export async function updateAsanaConfig(config: Partial<AsanaConfig>): Promise<AsanaConfig> {
  return fetchApi<AsanaConfig>('/integrations/asana/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// Test Asana connection
export async function testAsanaConnection(): Promise<{ success: boolean; user?: string; error?: string }> {
  return fetchApi('/integrations/asana/test');
}

// Get available Asana workspaces
export async function getAsanaWorkspaces(): Promise<AsanaWorkspace[]> {
  return fetchApi<AsanaWorkspace[]>('/integrations/asana/workspaces');
}

// Get projects for a workspace
export async function getAsanaProjects(workspaceGid: string): Promise<AsanaProject[]> {
  return fetchApi<AsanaProject[]>(`/integrations/asana/workspaces/${workspaceGid}/projects`);
}

// Get sync status
export async function getAsanaSyncStatus(): Promise<AsanaSyncStatus> {
  return fetchApi<AsanaSyncStatus>('/integrations/asana/status');
}

// Trigger manual sync
export async function triggerAsanaSync(direction: 'both' | 'inbound' | 'outbound' = 'both'): Promise<{
  success: boolean;
  created: number;
  updated: number;
  errors: number;
}> {
  return fetchApi('/integrations/asana/sync', {
    method: 'POST',
    body: JSON.stringify({ direction }),
  });
}

// Save project mapping
export async function saveProjectMapping(
  lifeguideProject: string,
  mapping: AsanaProjectMapping
): Promise<void> {
  await fetchApi(`/integrations/asana/mappings/${encodeURIComponent(lifeguideProject)}`, {
    method: 'PUT',
    body: JSON.stringify(mapping),
  });
}

// Delete project mapping
export async function deleteProjectMapping(lifeguideProject: string): Promise<void> {
  await fetchApi(`/integrations/asana/mappings/${encodeURIComponent(lifeguideProject)}`, {
    method: 'DELETE',
  });
}

// ========================================
// Google Calendar Integration
// ========================================

// Types for Google Calendar configuration
export interface GoogleCalendarConfig {
  enabled: boolean;
  connected: boolean;
  calendarId: string | null;
  calendarName: string | null;
  googleEmail: string | null;
  syncDirection: 'bidirectional' | 'outbound' | 'inbound';
  conflictResolution: 'field_merge' | 'lifeguide_wins' | 'calendar_wins';
  lastSync: string | null;
  syncedEventCount: number;
}

export interface GoogleCalendar {
  id: string;
  name: string;
  primary: boolean;
  backgroundColor?: string;
}

export interface GoogleCalendarSyncStatus {
  status: 'ok' | 'error' | 'not_connected';
  syncedEvents: number;
  lastSync: string | null;
  enabled: boolean;
  googleEmail: string | null;
}

export interface GoogleCalendarAuthResponse {
  auth_url: string;
  state: string;
}

export interface GoogleCalendarCallbackResponse {
  status: string;
  email?: string;
  calendar_id?: string;
  calendar_name?: string;
  error?: string;
}

// Get OAuth authorization URL
export async function getGoogleCalendarAuthUrl(): Promise<GoogleCalendarAuthResponse> {
  return fetchApi<GoogleCalendarAuthResponse>('/integrations/google-calendar/auth/url');
}

// Handle OAuth callback
export async function handleGoogleCalendarCallback(
  code: string,
  state?: string
): Promise<GoogleCalendarCallbackResponse> {
  return fetchApi<GoogleCalendarCallbackResponse>('/integrations/google-calendar/auth/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
}

// Get current Google Calendar configuration
export async function getGoogleCalendarConfig(): Promise<GoogleCalendarConfig> {
  return fetchApi<GoogleCalendarConfig>('/integrations/google-calendar/config');
}

// Update Google Calendar configuration
export async function updateGoogleCalendarConfig(
  config: Partial<GoogleCalendarConfig>
): Promise<GoogleCalendarConfig> {
  return fetchApi<GoogleCalendarConfig>('/integrations/google-calendar/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// Get available calendars
export async function getGoogleCalendars(): Promise<GoogleCalendar[]> {
  return fetchApi<GoogleCalendar[]>('/integrations/google-calendar/calendars');
}

// Test Google Calendar connection
export async function testGoogleCalendarConnection(): Promise<{
  success: boolean;
  email?: string;
  error?: string;
}> {
  return fetchApi('/integrations/google-calendar/test');
}

// Get sync status
export async function getGoogleCalendarSyncStatus(): Promise<GoogleCalendarSyncStatus> {
  return fetchApi<GoogleCalendarSyncStatus>('/integrations/google-calendar/status');
}

// Trigger manual sync
export async function triggerGoogleCalendarSync(
  direction: 'both' | 'inbound' | 'outbound' = 'both'
): Promise<{
  status: string;
  results: {
    inbound?: { success: boolean; created: number; updated: number; errors: number };
    outbound?: { success: boolean; created: number; updated: number; errors: number };
  };
}> {
  return fetchApi('/integrations/google-calendar/sync', {
    method: 'POST',
    body: JSON.stringify({ direction }),
  });
}

// Disconnect Google Calendar
export async function disconnectGoogleCalendar(): Promise<{ status: string }> {
  return fetchApi('/integrations/google-calendar/disconnect', {
    method: 'POST',
  });
}

// ========================================
// GitHub Journal Integration (LogSeq/Obsidian)
// ========================================

// Types for GitHub Journal configuration
export type JournalFormat = 'logseq' | 'obsidian';
export type GitHubSyncDirection = 'bidirectional' | 'inbound' | 'outbound';

export interface GitHubJournalConfig {
  enabled: boolean;
  connected: boolean;
  repository: string | null;
  branch: string;
  journalPath: string;
  journalFormat: JournalFormat;
  syncDirection: GitHubSyncDirection;
  syncDaysBack: number;
  lastSync: string | null;
  syncedFileCount: number;
}

export interface GitHubJournalSyncStatus {
  status: 'ok' | 'error' | 'not_connected';
  syncedFiles: number;
  lastSync: string | null;
  enabled: boolean;
  repository: string | null;
  lastSyncStatus: string | null;
}

export interface GitHubJournalTestResult {
  success: boolean;
  repository?: string;
  branch?: string;
  journalPath?: string;
  journalFilesFound?: number;
  error?: string;
}

export interface GitHubJournalSyncResult {
  status: string;
  results: {
    inbound?: {
      success: boolean;
      files_synced: number;
      files_created: number;
      files_updated: number;
      errors: number;
    };
    outbound?: {
      success: boolean;
      files_synced: number;
      files_created: number;
      files_updated: number;
      errors: number;
    };
  };
}

// Get current GitHub Journal configuration
export async function getGitHubJournalConfig(): Promise<GitHubJournalConfig> {
  return fetchApi<GitHubJournalConfig>('/integrations/github-journal/config');
}

// Update GitHub Journal configuration
export async function updateGitHubJournalConfig(
  config: Partial<GitHubJournalConfig> & { githubToken?: string }
): Promise<GitHubJournalConfig> {
  return fetchApi<GitHubJournalConfig>('/integrations/github-journal/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// Test GitHub connection
export async function testGitHubJournalConnection(): Promise<GitHubJournalTestResult> {
  return fetchApi<GitHubJournalTestResult>('/integrations/github-journal/test');
}

// Get sync status
export async function getGitHubJournalSyncStatus(): Promise<GitHubJournalSyncStatus> {
  return fetchApi<GitHubJournalSyncStatus>('/integrations/github-journal/status');
}

// Trigger manual sync
export async function triggerGitHubJournalSync(
  direction: 'both' | 'inbound' | 'outbound' = 'both'
): Promise<GitHubJournalSyncResult> {
  return fetchApi<GitHubJournalSyncResult>('/integrations/github-journal/sync', {
    method: 'POST',
    body: JSON.stringify({ direction }),
  });
}

// Disconnect GitHub Journal integration
export async function disconnectGitHubJournal(): Promise<{ status: string }> {
  return fetchApi('/integrations/github-journal/disconnect', {
    method: 'POST',
  });
}

// ========================================
// Spreadsheet Import Integration (Jira)
// ========================================

// Types for Spreadsheet Import configuration
export type SpreadsheetFileSource = 'upload' | 'google_drive' | 'onedrive' | 'github';
export type SpreadsheetSyncDirection = 'inbound' | 'outbound' | 'bidirectional';

export interface ColumnMapping {
  issueKey: string;
  summary: string;
  issueType: string;
  parentKey: string;
  epicLink: string;
  project: string;
  status: string;
  resolution: string;
  created: string;
  updated: string;
  dueDate: string;
  resolved: string;
  description: string;
  priority: string;
  assignee: string;
  reporter: string;
  labels: string;
  sprint: string;
  storyPoints: string;
  customFields: Record<string, string>;
}

export interface SpreadsheetImportConfig {
  _id: string;
  userId: string;
  name: string;
  fileSource: SpreadsheetFileSource;
  cloudFileId: string | null;
  cloudFileName: string | null;
  cloudFolderPath: string | null;
  googleDriveFileId: string | null;
  googleDriveFolderId: string | null;
  onedriveItemId: string | null;
  onedriveDriveId: string | null;
  // GitHub specific (for two-stage merge workflow)
  githubRepository: string | null;
  githubBranch: string;
  githubToken: string | null;
  githubJiraFolder: string;
  githubExportFilename: string;
  githubMasterFilename: string;
  enabled: boolean;
  syncDirection: SpreadsheetSyncDirection;
  autoSyncEnabled: boolean;
  syncIntervalHours: number;
  columnMapping: ColumnMapping;
  defaultProjectTopic: string | null;
  sheetName: string | null;
  headerRow: number;
  statusMapping: Record<string, string>;
  priorityMapping: Record<string, string>;
  lastSync: string | null;
  lastSyncStatus: string | null;
  lastFileModified: string | null;
  totalRowsImported: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SpreadsheetImportStats {
  epicsCreated: number;
  epicsUpdated: number;
  storiesCreated: number;
  storiesUpdated: number;
  tasksCreated: number;
  tasksUpdated: number;
  skipped: number;
  errors: number;
  errorDetails: Array<{ key: string; error: string }>;
}

export interface SpreadsheetImportStatus {
  configId?: string;
  name?: string;
  enabled?: boolean;
  fileSource?: SpreadsheetFileSource;
  lastSync: string | null;
  lastSyncStatus: string | null;
  totalRowsImported?: number;
  mappingCount?: number;
  syncDirection?: SpreadsheetSyncDirection;
  // For summary
  totalConfigs?: number;
  configs?: Array<{
    id: string;
    name: string;
    enabled: boolean;
    lastSync: string | null;
    totalRows: number;
  }>;
}

export interface SpreadsheetPreviewResult {
  totalRows: number;
  epics: number;
  stories: number;
  subtasks: number;
  other: number;
  existingMappings: number;
  newRows: number;
  sampleRows: Array<{
    key: string;
    summary: string;
    issueType: string;
    parentKey: string | null;
    status: string | null;
    exists: boolean;
  }>;
  columns: string[];
}

export interface SpreadsheetFormatInfo {
  filename: string;
  isExcel: boolean;
  sheets: string[];
  columns: string[];
  rowCount: number;
  suggestedMapping: ColumnMapping | null;
}

export interface SpreadsheetUploadResult {
  status: 'success' | 'error';
  configId: string;
  stats: SpreadsheetImportStats;
  error: string | null;
}

// Get all spreadsheet import configurations
export async function getSpreadsheetConfigs(): Promise<{ configs: SpreadsheetImportConfig[] }> {
  return fetchApi<{ configs: SpreadsheetImportConfig[] }>('/integrations/spreadsheet-import/configs');
}

// Get a specific configuration
export async function getSpreadsheetConfig(configId: string): Promise<SpreadsheetImportConfig> {
  return fetchApi<SpreadsheetImportConfig>(`/integrations/spreadsheet-import/configs/${configId}`);
}

// Create new configuration
export async function createSpreadsheetConfig(config: {
  name: string;
  fileSource?: SpreadsheetFileSource;
  defaultProjectTopic?: string;
  syncDirection?: SpreadsheetSyncDirection;
  columnMapping?: Partial<ColumnMapping>;
  statusMapping?: Record<string, string>;
  priorityMapping?: Record<string, string>;
}): Promise<SpreadsheetImportConfig> {
  return fetchApi<SpreadsheetImportConfig>('/integrations/spreadsheet-import/configs', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

// Update configuration
export async function updateSpreadsheetConfig(
  configId: string,
  config: Partial<SpreadsheetImportConfig>
): Promise<SpreadsheetImportConfig> {
  return fetchApi<SpreadsheetImportConfig>('/integrations/spreadsheet-import/config', {
    method: 'PUT',
    body: JSON.stringify({ configId, ...config }),
  });
}

// Delete configuration
export async function deleteSpreadsheetConfig(configId: string): Promise<{ status: string }> {
  return fetchApi(`/integrations/spreadsheet-import/configs/${configId}`, {
    method: 'DELETE',
  });
}

// Upload and import a spreadsheet file
export async function uploadSpreadsheet(
  file: File,
  options?: {
    configId?: string;
    defaultProjectTopic?: string;
  }
): Promise<SpreadsheetUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.configId) {
    formData.append('configId', options.configId);
  }
  if (options?.defaultProjectTopic) {
    formData.append('defaultProjectTopic', options.defaultProjectTopic);
  }

  // Use fetchApi with FormData (no Content-Type header, browser sets it)
  return fetchApi<SpreadsheetUploadResult>('/integrations/spreadsheet-import/upload', {
    method: 'POST',
    body: formData,
    headers: {}, // Let browser set Content-Type for FormData
  });
}

// Preview what would be imported
export async function previewSpreadsheetImport(
  file: File,
  configId?: string
): Promise<SpreadsheetPreviewResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (configId) {
    formData.append('configId', configId);
  }

  return fetchApi<SpreadsheetPreviewResult>('/integrations/spreadsheet-import/preview', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}

// Detect file format and suggest column mapping
export async function detectSpreadsheetFormat(file: File): Promise<SpreadsheetFormatInfo> {
  const formData = new FormData();
  formData.append('file', file);

  return fetchApi<SpreadsheetFormatInfo>('/integrations/spreadsheet-import/detect', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}

// Get sync status
export async function getSpreadsheetStatus(configId?: string): Promise<SpreadsheetImportStatus> {
  const url = configId
    ? `/integrations/spreadsheet-import/status?configId=${configId}`
    : '/integrations/spreadsheet-import/status';
  return fetchApi<SpreadsheetImportStatus>(url);
}

// Link a Google Drive file
export async function linkGoogleDriveSpreadsheet(options: {
  fileId: string;
  fileName?: string;
  folderPath?: string;
  configId?: string;
}): Promise<{ status: string; configId: string; config: SpreadsheetImportConfig }> {
  return fetchApi('/integrations/spreadsheet-import/link-google-drive', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

// Link a OneDrive file
export async function linkOnedriveSpreadsheet(options: {
  itemId: string;
  driveId?: string;
  fileName?: string;
  folderPath?: string;
  configId?: string;
}): Promise<{ status: string; configId: string; config: SpreadsheetImportConfig }> {
  return fetchApi('/integrations/spreadsheet-import/link-onedrive', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

// Trigger sync for linked cloud file
export async function triggerSpreadsheetSync(configId: string): Promise<{
  status: string;
  message?: string;
  configId: string;
  stats?: SpreadsheetImportStats;
}> {
  return fetchApi('/integrations/spreadsheet-import/sync', {
    method: 'POST',
    body: JSON.stringify({ configId }),
  });
}

// ==============================
// GitHub Integration (Two-Stage Merge Workflow)
// ==============================

export interface GitHubLinkResult {
  status: string;
  configId: string;
  repository: string;
  branch: string;
  jiraFolder: string;
  exportFileExists: boolean;
  masterFileExists: boolean;
  repoInfo: {
    name: string | null;
    fullName: string | null;
    owner: string | null;
    private: boolean | null;
  } | null;
}

export interface GitHubSyncResult {
  success: boolean;
  error?: string;
  mergeResult?: {
    success: boolean;
    timestamp: string;
    stats: {
      added: number;
      updated: number;
      preserved: number;
      removed: number;
      unchanged: number;
      totalProcessed: number;
    };
    conflicts?: {
      totalConflicts: number;
      autoResolvedCount: number;
      manualRequiredCount: number;
    };
    deltas?: {
      totalChanges: number;
      summary: {
        added: number;
        updated: number;
        deleted: number;
      };
    };
  };
  importResult?: {
    success: boolean;
    stats: SpreadsheetImportStats;
  };
  filesWritten?: {
    masterFile: string;
    conflictReport: string;
    deltaReport: string;
    syncState: string;
  };
}

export interface GitHubStatusResult {
  configId: string;
  repository: string;
  branch: string;
  lastSync: string | null;
  lastSyncStatus: string | null;
  totalRowsImported: number;
  exportFile: {
    exists: boolean;
    path: string;
    size: number;
    sha: string | null;
  };
  masterFile: {
    exists: boolean;
    path: string;
  };
  syncState: Record<string, unknown> | null;
  reportPaths: {
    conflicts: string;
    deltas: string;
  };
}

export interface GitHubTestResult {
  success: boolean;
  error?: string;
  repository?: string;
  branch?: string;
  repoInfo?: {
    name: string | null;
    owner: string | null;
    private: boolean | null;
    description: string | null;
  };
  jiraFolder?: {
    path: string;
    files: Array<{ name: string; path: string; size: number }>;
  };
  files?: {
    exportFile: { path: string; exists: boolean };
    masterFile: { path: string; exists: boolean };
  };
}

// Link a GitHub repository for JIRA sync
export async function linkGitHubRepository(options: {
  repository: string;
  githubToken: string;
  branch?: string;
  jiraFolder?: string;
  name?: string;
  configId?: string;
}): Promise<GitHubLinkResult> {
  return fetchApi('/integrations/spreadsheet-import/link-github', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

// Trigger GitHub sync (two-stage merge workflow)
export async function triggerGitHubSync(configId: string): Promise<GitHubSyncResult> {
  return fetchApi('/integrations/spreadsheet-import/github-sync', {
    method: 'POST',
    body: JSON.stringify({ configId }),
  });
}

// Get GitHub sync status
export async function getGitHubSyncStatus(configId: string): Promise<GitHubStatusResult> {
  return fetchApi(`/integrations/spreadsheet-import/github-status?configId=${configId}`);
}

// Test GitHub connection without saving
export async function testGitHubConnection(options: {
  repository: string;
  githubToken: string;
  branch?: string;
  jiraFolder?: string;
}): Promise<GitHubTestResult> {
  return fetchApi('/integrations/spreadsheet-import/github-test', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

// Get conflict report from GitHub
export async function getGitHubConflictReport(configId: string): Promise<{
  success: boolean;
  report: string | null;
  path?: string;
  message?: string;
}> {
  return fetchApi(`/integrations/spreadsheet-import/conflict-report?configId=${configId}`);
}

// Get delta report from GitHub
export async function getGitHubDeltaReport(configId: string): Promise<{
  success: boolean;
  report: string | null;
  path?: string;
  message?: string;
}> {
  return fetchApi(`/integrations/spreadsheet-import/delta-report?configId=${configId}`);
}

// Prepare master file for JIRA re-import
export async function prepareJiraImportFile(configId: string): Promise<{
  success: boolean;
  csvContent?: string;
  filename?: string;
  error?: string;
}> {
  return fetchApi(`/integrations/spreadsheet-import/prepare-jira-import?configId=${configId}`);
}

